<script setup lang="ts">
import { fixNumber } from "@/components/Mission/MissionStat";
import { useHouseSession } from "@/features/dream-home/store/houseSession";
import { getRandomSubjectList } from "@/features/dream-home/store/referSubject";
import { showLoadingToast } from "vant";
import { onMounted } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits<{
  (e: "save"): void;
}>();

const MAX_SUBJECT_LENGTH = 5000;

const houseStore = useHouseSession();
const { t } = useI18n();

const showConfirmSaveSubject = ref(false);
const subject = ref("");
const referSubjectList = ref<
  {
    id: number;
    trigger: string;
  }[]
>(getRandomSubjectList());

const subjectLength = computed(() => {
  if (!subject.value) return 0;
  return subject.value.length || 0;
});

const extraPower = computed(() => {
  if (!subject.value) return 0;
  return fixNumber(Math.ceil((subjectLength.value - 100) / 100) * 0.1);
});

onMounted(() => {
  subject.value = houseStore.houseSession?.subject || "";
});

const clickSaveSubject = async () => {
  if (!houseStore.houseSession) return;
  if (subject.value === "") return;

  showConfirmSaveSubject.value = true;
};

const switchReferSubject = () => {
  referSubjectList.value = getRandomSubjectList();
};

const clickReferSubject = async (trigger: string) => {
  if (subject.value === trigger) return;

  subject.value = trigger;
};

const clickConfirmSaveSubject = async (isClean: boolean) => {
  const promises = [houseStore.updateHouseSessionSubject(subject.value)];

  if (isClean) {
    promises.push(houseStore.deleteHouseSessionMessage());
  }

  const loading = showLoadingToast(t("Message.saving"));
  try {
    await Promise.all(promises);
    // 更新subjectUpdateTime，用于下次请求时判断是否需要做水位检查
    houseStore.subjectUpdateTime = new Date(
      houseStore.houseSession!.dtUpdate
    ).getTime();
    showConfirmSaveSubject.value = false;
    emit("save");
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="h-full overflow-y-auto">
    <van-cell-group inset class="border mt-2 bg-base-100">
      <van-field
        v-if="houseStore.houseSession"
        v-model="subject"
        rows="1"
        autosize
        type="textarea"
        placeholder=""
        class="bg-base-100"
      />
      <div class="flex justify-between items-center px-4 py-2">
        <div class="text-base-content/60 text-xs" v-if="subjectLength <= 100">
          {{ subjectLength }} / {{ MAX_SUBJECT_LENGTH }}
        </div>
        <div v-else class="text-base-content/60 text-xs">
          <p>
            {{ subjectLength }} / {{ MAX_SUBJECT_LENGTH }} ({{
              $t("house.subjectPanel.charCostTip")
            }})
          </p>
          <p class="text-base-content/60 text-xs flex items-center gap-1">
            {{ $t("house.subjectPanel.charCostTip2") }}
            <PowerIcon class="icon-xs relative -top-[1px]" />
            {{ extraPower }}
          </p>
        </div>
        <div>
          <button
            class="btn btn-primary btn-xs rounded"
            @click="clickSaveSubject"
          >
            <i class="iconify mingcute--save-2-line" />
            {{ $t("Buttons.save") }}
          </button>
        </div>
      </div>
    </van-cell-group>

    <div class="red-dot ml-6 mr-4 mt-2 flex justify-between items-center">
      {{ $t("house.referSubject") }}
      <button class="btn btn-outline btn-xs" @click="switchReferSubject">
        <i class="iconify mingcute--refresh-1-line" />
        {{ $t("Buttons.switch") }}
      </button>
    </div>

    <div class="flex flex-wrap gap-2 px-4 py-2">
      <div
        v-for="item in referSubjectList"
        :key="item.id"
        class="bg-neutral text-neutral-content rounded-lg px-2 py-1 text-xs cursor-pointer"
        @click="clickReferSubject(item.trigger)"
      >
        {{ item.trigger }}
      </div>
    </div>
    <VanPopup v-model:show="showConfirmSaveSubject" class="py-5 px-4 rounded">
      <div class="flex flex-col gap-4">
        <p>{{ $t("house.subjectPanel.changeSubjectTip") }}</p>
        <div class="flex gap-2 justify-center">
          <button
            class="btn btn-outline btn-sm"
            @click="showConfirmSaveSubject = false"
          >
            {{ $t("house.subjectPanel.cancel") }}
          </button>
          <button
            class="btn btn-outline btn-sm"
            @click="clickConfirmSaveSubject(true)"
          >
            {{ $t("house.subjectPanel.changeAndClear") }}
          </button>
          <button
            class="btn btn-primary btn-sm"
            @click="clickConfirmSaveSubject(false)"
          >
            {{ $t("house.subjectPanel.changeOnly") }}
          </button>
        </div>
      </div>
    </VanPopup>
  </div>
</template>
